import { useState, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import DatePicker from 'react-datepicker';
import {
  Loader2,
  RefreshCw,
  Filter,
  X,
  TrendingUp,
  Building2,
  Users,
  ChevronDown,
  ChevronRight,
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  BarChart3,
  Download,
  Sparkles,
  Layers,
  Info,
} from 'lucide-react';
import * as XLSX from 'xlsx';

// ── Types ────────────────────────────────────────────
interface CategoryTotals {
  A: number;
  B: number;
  C: number;
  total: number;
  uncategorized?: number;
}

interface DepartmentRow {
  department: string;
  groupName: string;
  totalIdeas: number;
  categoryA: number;
  categoryB: number;
  categoryC: number;
  approvedByIC: number;
  approvedByEC: number;
  totalSavings: number;
  pendingSavings: number;
}

interface CommitteeMemberRow {
  memberId: string;
  memberName: string;
  memberEmail: string;
  role: string;
  assignedCount: number;
  evaluatedCount: number;
  pendingCount: number;
}

interface AdvancedReportData {
  generatedAt: string;
  filters: Record<string, string | null>;
  categoryWise: {
    totalReceived: CategoryTotals & { uncategorized: number };
    approvedByIC: CategoryTotals;
    approvedByEC: CategoryTotals;
  };
  savings: {
    target: number;
    totalSavingsApproved: number;
    savingsVsTargetPct: number;
    savingsWaitingEC: number;
    savingsApprovedICNotImplemented: number;
    icApprovedEcRejectedCount: number;
    icApprovedEcRejectedSavings: number;
  };
  departmentBreakdown: DepartmentRow[];
  departmentGroups: { id: string; name: string; departments: string[] }[];
  committeePerformance: CommitteeMemberRow[];
}

// ── Helpers ──────────────────────────────────────────
function formatAmount(val: number): string {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)} K`;
  return `₹${val.toLocaleString('en-IN')}`;
}

const ROLE_LABELS: Record<string, string> = {
  COMMITTEE_MEMBER: 'Committee Member',
  LEADER: 'Committee Leader',
  COMMITTEE_HEAD: 'Committee Head',
  EXTERNAL_COMMITTEE: 'External Committee',
  EXECUTION_COMMITTEE: 'Execution Committee',
};

const ROLE_COLORS: Record<string, string> = {
  COMMITTEE_MEMBER: 'bg-blue-100 text-blue-700',
  LEADER: 'bg-purple-100 text-purple-700',
  COMMITTEE_HEAD: 'bg-amber-100 text-amber-700',
  EXTERNAL_COMMITTEE: 'bg-emerald-100 text-emerald-700',
  EXECUTION_COMMITTEE: 'bg-rose-100 text-rose-700',
};

// ── Sub-components ───────────────────────────────────
function SectionHeader({ icon: Icon, title, subtitle, children }: { icon: any; title: string; subtitle?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mt-10 mb-5">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#c29547]/20 to-[#c29547]/5 shadow-sm">
          <Icon className="w-5 h-5 text-[#c29547]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function CategoryCard({
  label,
  data,
}: {
  label: string;
  data: CategoryTotals & { uncategorized?: number };
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-all h-full flex flex-col justify-between group">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
      </div>
      <div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Cat A', value: data.A, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { label: 'Cat B', value: data.B, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-100' },
            { label: 'Cat C', value: data.C, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-100' },
            { label: 'Total', value: data.total, color: 'text-gray-900', bg: 'bg-gray-50', border: 'border-gray-200' },
          ].map(item => (
            <div key={item.label} className={`text-center p-2.5 rounded-lg border ${item.bg} ${item.border} group-hover:scale-[1.02] transition-transform duration-200`}>
              <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
              <p className="text-[9px] font-bold text-gray-500 mt-1 uppercase tracking-wider">{item.label}</p>
            </div>
          ))}
        </div>
        {'uncategorized' in data && data.uncategorized! > 0 && (
          <p className="text-[11px] text-gray-400 mt-3 text-right italic">
            + {data.uncategorized} uncategorized
          </p>
        )}
      </div>
    </div>
  );
}

function SavingsKpiCard({
  icon: Icon,
  label,
  value,
  subtitle,
  iconColor = 'text-gray-500',
  iconBg = 'bg-gray-50',
  tooltipValue,
  tooltipLabel,
}: {
  icon: any;
  label: string;
  value: string | number;
  subtitle?: string;
  iconColor?: string;
  iconBg?: string;
  tag?: string;
  tooltipValue?: number;
  tooltipLabel?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all h-full flex flex-col justify-between">
      <div className="flex items-start gap-3 mb-6">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border border-gray-100 flex-shrink-0 ${iconBg} ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-sm font-medium text-gray-700 leading-snug">{label}</p>
      </div>
      <div>
        <div className="flex items-center gap-2 group relative w-max">
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {tooltipValue !== undefined && (
            <>
              <div className="w-[18px] h-[18px] rounded-full bg-blue-50 flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors">
                <Info className="w-[10px] h-[10px] text-blue-500" strokeWidth={3} />
              </div>
              <div className="absolute top-full left-0 mt-2 w-max px-3 py-2 bg-[#1e293b] text-white text-left rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl z-50 pointer-events-none">
                <div className="absolute bottom-full left-4 border-[5px] border-transparent border-b-[#1e293b]"></div>
                <p className="text-[11px] font-medium text-gray-300 mb-0.5">{tooltipLabel || label}</p>
                <p className="font-bold text-sm tracking-wide">₹{tooltipValue.toLocaleString('en-IN')}</p>
              </div>
            </>
          )}
        </div>
        {subtitle && <p className="text-xs text-gray-400 mt-2">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────
export function AdvancedReports() {
  // Filter state
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCommitteeRole, setSelectedCommitteeRole] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (selectedGroup) params.departmentGroup = selectedGroup;
    if (selectedDept) params.department = selectedDept;
    if (selectedCategory) params.category = selectedCategory;
    if (selectedCommitteeRole) params.committeeRole = selectedCommitteeRole;
    if (selectedMemberId) params.committeeMemberId = selectedMemberId;
    if (startDate) params.startDate = startDate.toISOString().split('T')[0];
    if (endDate) params.endDate = endDate.toISOString().split('T')[0];
    return params;
  }, [selectedGroup, selectedDept, selectedCategory, selectedCommitteeRole, selectedMemberId, startDate, endDate]);

  const hasActiveFilters = Object.keys(queryParams).length > 0;

  const { data, isLoading, refetch, isFetching } = useQuery<AdvancedReportData>({
    queryKey: ['advancedReport', queryParams],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/advanced', { params: queryParams });
      return res.data.data;
    },
  });

  // Fetch committee members for the dropdown
  const { data: committeeUsersForFilter } = useQuery({
    queryKey: ['committeeUsers', selectedCommitteeRole],
    queryFn: async () => {
      const res = await apiClient.get('/admin/users', {
        params: { role: selectedCommitteeRole || undefined, limit: 200 },
      });
      return res.data?.data?.items || res.data?.data || [];
    },
    enabled: !!selectedCommitteeRole,
  });

  // Departments filtered by selected group
  const filteredDepartments = useMemo(() => {
    if (!data?.departmentGroups) return [];
    if (selectedGroup) {
      const group = data.departmentGroups.find(g => g.name === selectedGroup);
      return group?.departments?.sort() || [];
    }
    const allDepts = new Set<string>();
    data.departmentGroups.forEach(g => g.departments.forEach(d => allDepts.add(d)));
    return Array.from(allDepts).sort();
  }, [data?.departmentGroups, selectedGroup]);

  // Group department breakdown by groupName
  const groupedDepartments = useMemo(() => {
    if (!data?.departmentBreakdown) return new Map<string, DepartmentRow[]>();
    const map = new Map<string, DepartmentRow[]>();
    for (const row of data.departmentBreakdown) {
      const group = row.groupName || 'Ungrouped';
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(row);
    }
    return map;
  }, [data?.departmentBreakdown]);

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const expandAll = () => setExpandedGroups(new Set(groupedDepartments.keys()));
  const collapseAll = () => setExpandedGroups(new Set());

  const resetFilters = () => {
    setSelectedGroup('');
    setSelectedDept('');
    setSelectedCategory('');
    setSelectedCommitteeRole('');
    setSelectedMemberId('');
    setStartDate(null);
    setEndDate(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    const catData = [
      ['', 'Cat A', 'Cat B', 'Cat C', 'Total'],
      ['Total Received', data.categoryWise.totalReceived.A, data.categoryWise.totalReceived.B, data.categoryWise.totalReceived.C, data.categoryWise.totalReceived.total],
      ['IC Approved', data.categoryWise.approvedByIC.A, data.categoryWise.approvedByIC.B, data.categoryWise.approvedByIC.C, data.categoryWise.approvedByIC.total],
      ['EC Approved', data.categoryWise.approvedByEC.A, data.categoryWise.approvedByEC.B, data.categoryWise.approvedByEC.C, data.categoryWise.approvedByEC.total],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(catData), 'Category Summary');

    const deptHeaders = ['Group', 'Department', 'Total Ideas', 'Cat A', 'Cat B', 'Cat C', 'IC Approved', 'EC Approved', 'Total Savings', 'Pending Savings'];
    const deptRows = data.departmentBreakdown.map(d => [
      d.groupName, d.department, d.totalIdeas, d.categoryA, d.categoryB, d.categoryC,
      d.approvedByIC, d.approvedByEC, d.totalSavings, d.pendingSavings,
    ]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([deptHeaders, ...deptRows]), 'Department Breakdown');

    const cmHeaders = ['Name', 'Email', 'Role', 'Assigned', 'Evaluated', 'Pending'];
    const cmRows = data.committeePerformance.map(m => [
      m.memberName, m.memberEmail, ROLE_LABELS[m.role] || m.role,
      m.assignedCount, m.evaluatedCount, m.pendingCount,
    ]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cmHeaders, ...cmRows]), 'Committee Performance');

    XLSX.writeFile(wb, `Advanced_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (isLoading) {
    return (
      <Layout title="Advanced Reports">
        <div className="flex flex-col items-center justify-center h-80 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-[#c29547]/20 border-t-[#c29547] animate-spin" />
            <Sparkles className="w-6 h-6 text-[#c29547] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-sm text-gray-500 font-medium">Generating advanced report...</p>
        </div>
      </Layout>
    );
  }

  const s = data?.savings;
  const cw = data?.categoryWise;
  const targetInRupees = (s?.target ?? 680) * 100000;
  const progressPct = Math.min(s?.savingsVsTargetPct ?? 0, 100);

  return (
    <Layout title="Advanced Reports">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#c29547] to-[#a17833] shadow-md">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Advanced Reports</h1>
                <p className="text-sm text-gray-400 mt-0.5">
                  Department-wise breakdown, savings KPIs & committee performance
                  {data?.generatedAt && (
                    <span className="inline-flex items-center gap-1 ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                      <Clock className="w-3 h-3" />
                      {new Date(data.generatedAt).toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-white border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ── Filter Panel (Horizontal) ── */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-8">
          <div className="p-4 flex flex-wrap items-center gap-3">
            {/* Department Group */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all relative">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={selectedGroup}
                onChange={e => { setSelectedGroup(e.target.value); setSelectedDept(''); }}
                className="bg-transparent text-gray-600 font-medium outline-none appearance-none min-w-[120px] pr-5 cursor-pointer"
              >
                <option value="">All Groups</option>
                {(data?.departmentGroups || []).map(g => (
                  <option key={g.id} value={g.name}>{g.name}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Department */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all relative">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={selectedDept}
                onChange={e => setSelectedDept(e.target.value)}
                className="bg-transparent text-gray-600 font-medium outline-none appearance-none min-w-[120px] pr-5 cursor-pointer"
              >
                <option value="">All Departments</option>
                {filteredDepartments.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Category */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all relative">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="bg-transparent text-gray-600 font-medium outline-none appearance-none min-w-[120px] pr-5 cursor-pointer"
              >
                <option value="">All Categories</option>
                <option value="A">Category A</option>
                <option value="B">Category B</option>
                <option value="C">Category C</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Committee Role */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all relative">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={selectedCommitteeRole}
                onChange={e => { setSelectedCommitteeRole(e.target.value); setSelectedMemberId(''); }}
                className="bg-transparent text-gray-600 font-medium outline-none appearance-none min-w-[120px] pr-5 cursor-pointer"
              >
                <option value="">All Roles</option>
                {Object.entries(ROLE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Committee Member */}
            {selectedCommitteeRole && (
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all relative">
                <Filter className="w-3.5 h-3.5 text-gray-400" />
                <select
                  value={selectedMemberId}
                  onChange={e => setSelectedMemberId(e.target.value)}
                  className="bg-transparent text-gray-600 font-medium outline-none appearance-none min-w-[140px] pr-5 cursor-pointer"
                >
                  <option value="">All Members</option>
                  {(committeeUsersForFilter || []).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            )}

            {/* Date Range */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus-within:ring-2 focus-within:ring-[#c29547]/20 transition-all">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="bg-transparent text-gray-600 font-medium outline-none w-[90px] cursor-pointer"
                isClearable
              />
              <span className="text-gray-400 text-xs font-medium">to</span>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                minDate={startDate || undefined}
                className="bg-transparent text-gray-600 font-medium outline-none w-[90px] cursor-pointer"
                isClearable
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-auto"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* ── Section 1: Category-wise Summary ── */}
        {cw && (
          <>
            <SectionHeader icon={BarChart3} title="Category-wise Summary" subtitle="Ideas grouped by evaluation category (A / B / C)" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
              <CategoryCard
                label="Total Ideas Received"
                data={cw.totalReceived}
              />
              <CategoryCard
                label="Approved by Internal Committee"
                data={cw.approvedByIC}
              />
              <CategoryCard
                label="Approved by External Committee"
                data={cw.approvedByEC}
              />
            </div>
          </>
        )}

        {/* ── Section 2: Savings KPIs ── */}
        {s && (
          <>
            <SectionHeader icon={Target} title="Savings Dashboard" subtitle="Annual savings tracking against organizational target" />

            {/* Target progress bar */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-5 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                    <Target className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">(d) Total Savings vs Target</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Target is set to {s.target} Lakhs</p>
                  </div>
                </div>
              </div>

              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Achieved</p>
                  <div className="flex items-center gap-2 group relative w-max">
                    <p className="text-3xl font-bold text-gray-900">{formatAmount(s.totalSavingsApproved)}</p>
                    <div className="w-[18px] h-[18px] rounded-full bg-blue-50 flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors">
                      <Info className="w-[10px] h-[10px] text-blue-500" strokeWidth={3} />
                    </div>
                    <div className="absolute top-full left-0 mt-2 w-max px-3 py-2 bg-[#1e293b] text-white text-left rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl z-50 pointer-events-none">
                      <div className="absolute bottom-full left-4 border-[5px] border-transparent border-b-[#1e293b]"></div>
                      <p className="text-[11px] font-medium text-gray-300 mb-0.5">External Committee Approved</p>
                      <p className="font-bold text-sm tracking-wide">₹{s.totalSavingsApproved.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-500 mb-1">Target</p>
                  <p className="text-xl font-semibold text-gray-700">{formatAmount(targetInRupees)}</p>
                </div>
              </div>

              {/* Clean progress bar */}
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${Math.max(progressPct, 1)}%`,
                      backgroundColor: progressPct >= 100
                        ? '#10b981' // emerald-500
                        : progressPct >= 50
                          ? '#c29547' // brand gold
                          : '#f97316', // orange-500
                    }}
                  />
                </div>
                <div className={`font-semibold text-sm ${progressPct >= 100 ? 'text-emerald-600' : progressPct >= 50 ? 'text-[#c29547]' : 'text-orange-600'
                  }`}>
                  {s.savingsVsTargetPct}%
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <SavingsKpiCard
                icon={Clock}
                label="Savings Waiting for EC Approval"
                value={formatAmount(s.savingsWaitingEC)}
                iconColor="text-orange-500"
                iconBg="bg-orange-50"
              />
              <SavingsKpiCard
                icon={AlertTriangle}
                label="IC Approved, Not Yet Implemented"
                value={formatAmount(s.savingsApprovedICNotImplemented)}
                iconColor="text-blue-500"
                iconBg="bg-blue-50"
                subtitle="Pending head / external approval"
              />
              <SavingsKpiCard
                icon={XCircle}
                label="IC Approved & EC Rejected"
                value={s.icApprovedEcRejectedCount}
                iconColor="text-rose-500"
                iconBg="bg-rose-50"
                subtitle={`Savings: ${formatAmount(s.icApprovedEcRejectedSavings)}`}
              />
              <SavingsKpiCard
                icon={CheckCircle2}
                label="EC Approved Total Savings"
                value={formatAmount(s.totalSavingsApproved)}
                tooltipValue={s.totalSavingsApproved}
                tooltipLabel="External Committee Approved"
                iconColor="text-emerald-500"
                iconBg="bg-emerald-50"
              />
            </div>
          </>
        )}

        {/* ── Section 3: Department-wise Breakdown ── */}
        {data?.departmentBreakdown && (
          <>
            <SectionHeader icon={Building2} title="Department-wise Breakdown" subtitle="Sorted by total savings — highest to lowest">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{data.departmentBreakdown.length} departments</span>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button onClick={expandAll} className="text-xs text-gray-600 hover:text-[#c29547] px-2.5 py-1 rounded-md hover:bg-white transition-all font-medium">
                    Expand
                  </button>
                  <button onClick={collapseAll} className="text-xs text-gray-600 hover:text-gray-800 px-2.5 py-1 rounded-md hover:bg-white transition-all font-medium">
                    Collapse
                  </button>
                </div>
              </div>
            </SectionHeader>

            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
                      <th className="px-5 py-4 text-left text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Department</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Total</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Cat A</span>
                      </th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span>Cat B</span>
                      </th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Cat C</span>
                      </th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">IC ✓</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">EC ✓</th>
                      <th className="px-4 py-4 text-right text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Savings</th>
                      <th className="px-4 py-4 text-right text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(groupedDepartments.entries()).map(([groupName, rows]) => {
                      const isExpanded = expandedGroups.has(groupName);
                      const gt = rows.reduce(
                        (a, r) => ({
                          totalIdeas: a.totalIdeas + r.totalIdeas,
                          categoryA: a.categoryA + r.categoryA,
                          categoryB: a.categoryB + r.categoryB,
                          categoryC: a.categoryC + r.categoryC,
                          approvedByIC: a.approvedByIC + r.approvedByIC,
                          approvedByEC: a.approvedByEC + r.approvedByEC,
                          totalSavings: a.totalSavings + r.totalSavings,
                          pendingSavings: a.pendingSavings + r.pendingSavings,
                        }),
                        { totalIdeas: 0, categoryA: 0, categoryB: 0, categoryC: 0, approvedByIC: 0, approvedByEC: 0, totalSavings: 0, pendingSavings: 0 }
                      );

                      return (
                        <Fragment key={groupName}>
                          {/* Group header row */}
                          <tr
                            className="bg-gradient-to-r from-slate-50/80 to-white hover:from-slate-100/80 cursor-pointer transition-all border-b border-gray-50"
                            onClick={() => toggleGroup(groupName)}
                          >
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2.5">
                                <div className={`p-1 rounded-md transition-colors ${isExpanded ? 'bg-[#c29547]/10' : 'bg-gray-100'}`}>
                                  {isExpanded
                                    ? <ChevronDown className="w-3.5 h-3.5 text-[#c29547]" />
                                    : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  }
                                </div>
                                <span className="font-bold text-gray-800">{groupName}</span>
                                <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{rows.length}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 text-center font-bold text-gray-800">{gt.totalIdeas}</td>
                            <td className="px-4 py-3.5 text-center font-bold text-emerald-600">{gt.categoryA}</td>
                            <td className="px-4 py-3.5 text-center font-bold text-blue-600">{gt.categoryB}</td>
                            <td className="px-4 py-3.5 text-center font-bold text-amber-600">{gt.categoryC}</td>
                            <td className="px-4 py-3.5 text-center font-bold text-gray-700">{gt.approvedByIC}</td>
                            <td className="px-4 py-3.5 text-center font-bold text-gray-700">{gt.approvedByEC}</td>
                            <td className="px-4 py-3.5 text-right font-bold text-emerald-700">{formatAmount(gt.totalSavings)}</td>
                            <td className="px-4 py-3.5 text-right font-bold text-amber-600">{formatAmount(gt.pendingSavings)}</td>
                          </tr>

                          {/* Individual department rows */}
                          {isExpanded && rows.map((row, idx) => (
                            <tr
                              key={row.department}
                              className={`hover:bg-blue-50/40 transition-colors border-b border-gray-50/80 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                            >
                              <td className="px-5 py-3 pl-14">
                                <span className="text-gray-600">{row.department}</span>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-600">{row.totalIdeas}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`${row.categoryA > 0 ? 'text-emerald-600 font-semibold' : 'text-gray-300'}`}>{row.categoryA}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`${row.categoryB > 0 ? 'text-blue-600 font-semibold' : 'text-gray-300'}`}>{row.categoryB}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`${row.categoryC > 0 ? 'text-amber-600 font-semibold' : 'text-gray-300'}`}>{row.categoryC}</span>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-600">{row.approvedByIC}</td>
                              <td className="px-4 py-3 text-center text-gray-600">{row.approvedByEC}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={`font-semibold ${row.totalSavings > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>
                                  {row.totalSavings > 0 ? formatAmount(row.totalSavings) : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`${row.pendingSavings > 0 ? 'text-amber-500 font-medium' : 'text-gray-300'}`}>
                                  {row.pendingSavings > 0 ? formatAmount(row.pendingSavings) : '—'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}

                    {data.departmentBreakdown.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-16 text-center">
                          <Building2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                          <p className="text-gray-400 font-medium">No department data available</p>
                          <p className="text-xs text-gray-300 mt-1">Try adjusting your filters</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Section 4: Committee Performance ── */}
        {data?.committeePerformance && (
          <>
            <SectionHeader icon={Users} title="Committee Performance" subtitle="Assigned, evaluated, and pending ideas per committee member" />
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
                      <th className="px-5 py-4 text-left text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Member</th>
                      <th className="px-4 py-4 text-left text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Email</th>
                      <th className="px-4 py-4 text-left text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Role</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Assigned</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Evaluated</th>
                      <th className="px-4 py-4 text-center text-[11px] uppercase text-gray-500 font-semibold tracking-wider">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.committeePerformance.map((member, idx) => (
                      <tr
                        key={member.memberId}
                        className={`hover:bg-blue-50/40 transition-colors border-b border-gray-50/80 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c29547] to-[#a17833] flex items-center justify-center text-white text-xs font-bold shadow-sm">
                              {member.memberName?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className="font-semibold text-gray-800">{member.memberName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-gray-500 text-xs">{member.memberEmail}</td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_LABELS[member.role] || member.role}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold text-sm">
                            {member.assignedCount}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-sm">
                            {member.evaluatedCount}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-lg font-bold text-sm ${member.pendingCount > 0
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-gray-50 text-gray-300'
                            }`}>
                            {member.pendingCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {data.committeePerformance.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-16 text-center">
                          <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                          <p className="text-gray-400 font-medium">No committee members found</p>
                          <p className="text-xs text-gray-300 mt-1">Try adjusting your filters</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Bottom spacer */}
        <div className="h-12" />
      </div>
    </Layout >
  );
}
